import { isAbortError } from './abort-reasons.js';
import { TRANSIENT_READ_RETRY, type NetworkRetryPolicy } from './network-retry-policies.js';


export interface NetworkRetryOptions {
  policy?: NetworkRetryPolicy;
  retries?: number;
  retryDelayMs?: number;
  fetchImpl?: typeof fetch;
  wait?: (delayMs: number) => Promise<void>;
  isOnline?: () => boolean;
  shouldRetryResponse?: (response: Response) => boolean;
}

function defaultIsOnline(): boolean {
  return !('navigator' in globalThis) || navigator.onLine !== false;
}

export async function fetchWithNetworkRetry(
  input: RequestInfo | URL,
  init: RequestInit = {},
  options: NetworkRetryOptions = {},
): Promise<Response> {
  const policy = options.policy ?? TRANSIENT_READ_RETRY;
  const retries = Math.max(0, options.retries ?? policy.retries);
  const retryDelay = options.retryDelayMs === undefined
    ? policy.delayMs
    : (retryNumber: number) => Math.max(0, options.retryDelayMs ?? 0) * retryNumber;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const wait = options.wait ?? ((delayMs: number) => new Promise<void>((resolve) => setTimeout(resolve, delayMs)));
  const isOnline = options.isOnline ?? defaultIsOnline;
  const shouldRetryResponse = options.shouldRetryResponse ?? policy.shouldRetryResponse;

  for (let attempt = 0; ; attempt += 1) {
    try {
      const response = await fetchImpl(input, init);
      if (attempt < retries && shouldRetryResponse?.(response)) {
        await wait(retryDelay(attempt + 1));
        continue;
      }
      return response;
    } catch (error) {
      if (attempt >= retries || isAbortError(error) || !isOnline()) throw error;
      await wait(retryDelay(attempt + 1));
    }
  }
}
